"use client";

import { useEffect, useState } from "react";
import { isGoogleMapShortLink, mapPreviewForAddress, mapPreviewFromUrl, type PortalMapPreview } from "../lib/portalMap";
import { safeWebUrl } from "../lib/portalPresentation";
import { usePortalTranslations } from "../i18n/client";
import styles from "./PortalLocationMap.module.css";

export function PortalLocationMap({ mapUrl, address }: { mapUrl?: string | null; address?: string | null }) {
  const t = usePortalTranslations();
  const [resolved, setResolved] = useState<{ source: string; preview: PortalMapPreview | null } | null>(null);
  const link = safeWebUrl(mapUrl);
  const shortLink = isGoogleMapShortLink(link);
  const pending = shortLink && resolved?.source !== link;
  const directPreview = mapPreviewFromUrl(link);
  const resolvedPreview = resolved && resolved.source === link ? resolved.preview : null;
  const addressPreview = !pending ? mapPreviewForAddress(address) : null;
  const preview = directPreview ?? resolvedPreview ?? addressPreview;
  const addressFallback = Boolean(link && !directPreview && !resolvedPreview && addressPreview);
  const externalLink = link ?? (address?.trim() ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}` : undefined);

  useEffect(() => {
    if (!link || !isGoogleMapShortLink(link)) return;
    const controller = new AbortController();
    async function resolve() {
      let preview: PortalMapPreview | null = null;
      try {
        const response = await fetch(`/api/client-portal-map?${new URLSearchParams({ url: link! })}`, { signal: controller.signal });
        if (response.ok) {
          const data = await response.json() as { preview?: PortalMapPreview | null };
          preview = mapPreviewFromUrl(data.preview?.embedUrl);
        }
      } catch { /* Keep the original link available if Google cannot resolve the preview. */ }
      if (!controller.signal.aborted) setResolved({ source: link!, preview });
    }
    void resolve();
    return () => controller.abort();
  }, [link]);

  if (!externalLink && !preview) return null;
  return <div className={styles.map}>
    {preview ? <iframe
      className={styles.frame}
      src={preview.embedUrl}
      title={preview.label ? t("portal.map.namedTitle", {name: preview.label}) : t("portal.map.title")}
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      allowFullScreen
    /> : <div className={styles.placeholder} role={pending ? "status" : undefined}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m3 6 6-3 6 3 6-3v15l-6 3-6-3-6 3V6Z" /><path d="M9 3v15M15 6v15" /></svg>
      <p>{pending ? t("portal.map.loading") : t("portal.map.unavailable")}</p>
    </div>}
    <div className={styles.footer}>
      {addressFallback ? <span>{t("portal.map.addressFallback")}</span> : <span>{t("portal.map.directions")}</span>}
      {externalLink ? <a href={externalLink} target="_blank" rel="noopener noreferrer">{t("portal.map.open")} <span aria-hidden="true">↗</span></a> : null}
    </div>
  </div>;
}
